/**
 * Robot Diagnostics Service
 *
 * API client for Robot Diagnostics endpoints.
 *
 * Story 55: Diagnose robot response issues for a site
 */

import { authFetch } from '@/utils/auth-fetch';

// Types
export interface DiagnosticRun {
  id: string;
  site_id: string;
  site_name?: string;
  site_url?: string;
  status: 'running' | 'completed' | 'failed';
  health_score: number | null;
  critical_count: number;
  warning_count: number;
  passed_count: number;
  urls_tested: number;
  started_at: string;
  completed_at: string | null;
  created_by_email?: string;
  error_message?: string;
  checks?: DiagnosticCheck[];
  // AI Analysis (saved with the run)
  ai_analysis?: string | null;
  ai_analysis_at?: string | null;
}

export interface DiagnosticCheck {
  id: string;
  run_id: string;
  check_type: 'robots_txt' | 'meta_robots' | 'x_robots_tag' | 'canonical' | 'ua_comparison';
  url: string;
  user_agent?: string;
  severity: 'critical' | 'warning' | 'passed';
  title: string;
  details: Record<string, unknown>;
  recommendations: string[];
  created_at: string;
}

export interface DiagnosticHistory {
  id: string;
  status: string;
  health_score: number | null;
  critical_count: number;
  warning_count: number;
  passed_count: number;
  urls_tested: number;
  started_at: string;
  completed_at: string | null;
}

export interface CooldownStatus {
  allowed: boolean;
  remainingMinutes?: number;
  nextAvailable?: string;
}

// API Functions

/**
 * Run a new diagnostic for a site
 */
export async function runDiagnostic(siteId: string): Promise<{
  success: boolean;
  runId?: string;
  healthScore?: number;
  summary?: { critical: number; warning: number; passed: number };
  urlsTested?: number;
  error?: string;
  message?: string;
  nextAvailable?: string;
}> {
  const response = await authFetch('/api/robot-diagnostics/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId }),
  });

  if (response.status === 429) {
    const data = await response.json();
    return {
      success: false,
      error: 'cooldown',
      message: data.message,
      nextAvailable: data.nextAvailable,
    };
  }

  return response.json();
}

/**
 * Get a specific diagnostic run with checks
 */
export async function getDiagnosticRun(runId: string): Promise<DiagnosticRun> {
  const response = await authFetch(`/api/robot-diagnostics/runs/${runId}`);
  return response.json();
}

/**
 * Get diagnostic history for a site
 */
export async function getDiagnosticHistory(
  siteId: string,
  limit?: number
): Promise<{ history: DiagnosticHistory[] }> {
  const params = new URLSearchParams({ siteId });
  if (limit) params.append('limit', String(limit));

  const response = await authFetch(`/api/robot-diagnostics/history?${params}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Get the latest diagnostic run for a site
 */
export async function getLatestDiagnostic(siteId: string): Promise<DiagnosticRun | { hasRun: false; message: string }> {
  const response = await authFetch(`/api/robot-diagnostics/latest?siteId=${siteId}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Check cooldown status for a site
 */
export async function checkCooldown(siteId: string): Promise<CooldownStatus> {
  const response = await authFetch(`/api/robot-diagnostics/cooldown?siteId=${siteId}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Get severity color class (with dark mode support)
 */
export function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800';
    case 'warning':
      return 'text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800';
    case 'passed':
      return 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800';
    default:
      return 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700';
  }
}

/**
 * Get severity badge color
 */
export function getSeverityBadgeColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-500';
    case 'warning':
      return 'bg-amber-500';
    case 'passed':
      return 'bg-green-500';
    default:
      return 'bg-gray-500';
  }
}

/**
 * Get health score color
 */
export function getHealthScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Get health score background
 */
export function getHealthScoreBackground(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Format check type for display
 */
export function formatCheckType(checkType: string): string {
  const labels: Record<string, string> = {
    robots_txt: 'Robots.txt',
    meta_robots: 'Meta Robots',
    x_robots_tag: 'X-Robots-Tag',
    canonical: 'Canonical',
    ua_comparison: 'User Agent Comparison',
  };
  return labels[checkType] || checkType;
}

// AI Analysis Types
export interface AIAnalysisResult {
  success: boolean;
  analysis: string;
  siteUrl: string;
  siteName: string;
  hasRobotsTxt: boolean;
  hasDiagnosticRun: boolean;
  cost?: {
    inputCost: number;
    outputCost: number;
    totalCost: number;
  };
}

/**
 * Get AI analysis of robots.txt and diagnostic results
 */
export async function analyzeWithAI(
  siteId: string,
  runId?: string
): Promise<AIAnalysisResult> {
  const response = await authFetch('/api/robot-diagnostics/analyze-ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, runId }),
  });

  return response.json();
}

// Product Visibility Test Types
export interface ProductVisibilityCheck {
  name: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  [key: string]: unknown;
}

export interface ProductVisibilityResult {
  product: {
    name: string;
    url: string | null;
    found_in_kb: boolean;
  };
  checks: ProductVisibilityCheck[];
  ai_visibility: {
    analysis: string;
    cost?: {
      inputCost: number;
      outputCost: number;
      totalCost: number;
    };
  } | null;
  overall_status: 'visible' | 'blocked' | 'partial' | 'unknown';
}

/**
 * Test if a product is visible to search engines and AI
 */
export async function testProductVisibility(
  siteId: string,
  productQuery: string
): Promise<ProductVisibilityResult> {
  const response = await authFetch('/api/robot-diagnostics/test-product', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, productQuery }),
  });

  return response.json();
}

// Sitemap Health Types
export interface SitemapError {
  type: 'fetch_error' | 'xml_parse_error' | 'xml_malformed' | 'parse_error';
  message: string;
  url: string;
}

export interface SitemapTestResult {
  id: string;
  testedAt: string;
  status: 'success' | 'partial' | 'failed';
  urlsFound: number;
  durationMs: number;
  errors: SitemapError[];
  httpStatus: number | null;
  contentType: string | null;
}

export interface SitemapInventoryStats {
  totalUrls: number;
  crawled: number;
  uncrawled: number;
  coveragePct: number;
  lastSync: string | null;
}

export interface SitemapHealthData {
  sitemapUrl: string | null;
  latestTest: SitemapTestResult | null;
  inventory: SitemapInventoryStats | null;
}

export interface SitemapTestResponse {
  success: boolean;
  status: 'success' | 'partial' | 'failed';
  sitemapUrl: string;
  urlsFound: number;
  errors: SitemapError[];
  durationMs: number;
  httpStatus: number | null;
  contentType: string | null;
}

/**
 * Get sitemap health data for a site
 */
export async function getSitemapHealth(siteId: string): Promise<SitemapHealthData> {
  const response = await authFetch(`/api/robot-diagnostics/sitemap/health?siteId=${siteId}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Test sitemap for a site
 */
export async function testSitemap(siteId: string): Promise<SitemapTestResponse> {
  const response = await authFetch('/api/robot-diagnostics/sitemap/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Get status color for sitemap status
 */
export function getSitemapStatusColor(status: string): string {
  switch (status) {
    case 'success':
      return 'text-green-600 dark:text-green-400';
    case 'partial':
      return 'text-amber-600 dark:text-amber-400';
    case 'failed':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
}

/**
 * Get badge color for sitemap status
 */
export function getSitemapStatusBadgeColor(status: string): string {
  switch (status) {
    case 'success':
      return 'bg-green-500 text-white';
    case 'partial':
      return 'bg-amber-500 text-white';
    case 'failed':
      return 'bg-red-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
}
